import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DashboardContent } from "@/components/dashboard/DashboardContent";
import { supabase } from '@/integrations/supabase/client';
import { MainNavigation } from '@/components/layout/MainNavigation';
import { Footer } from '@/components/layout/Footer';
import { Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { getPEOVerificationStatus } from '@/services/auth.service';
import { useAdminStatus } from '@/hooks/useAdminStatus';


const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, userRole, user, isLoading: authLoading } = useAuth();
  const { isSuperAdmin, isLoading: isAdminLoading } = useAdminStatus();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forceRefresh, setForceRefresh] = useState(0);

  // Force refresh when returning to dashboard from application submitted page
  useEffect(() => {
    // Check if we're coming from the application submitted page
    if (location.state?.fromSubmission) {
      console.log('Detected return from submission page, forcing refresh');
      setForceRefresh(prev => prev + 1);
    }
  }, [location]);

  // Debug state to help troubleshoot
  const [debugInfo, setDebugInfo] = useState({
    hasCheckedAuth: false,
    authState: {
      isAuthenticated: false,
      userRole: null as string | null,
      userEmail: null as string | null
    }
  });

  // Check authentication and role
  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      try {
        console.log("Dashboard: Starting auth check");
        const { data } = await supabase.auth.getSession();
        
        // Save debug info
        setDebugInfo({
          hasCheckedAuth: true,
          authState: {
            isAuthenticated: !!data.session,
            userRole: userRole || null,
            userEmail: data.session?.user?.email || null
          }
        });
        
        if (!data.session) {
          console.log("Dashboard: No session found, redirecting to login");
          navigate('/login');
          return;
        }
        
        // Handle role-based redirects
        if (isSuperAdmin) {
          console.log("Dashboard: Super admin detected, redirecting to admin panel");
          navigate('/admin');
          return;
        } 
        
        if (userRole === 'peo') {
          console.log("Dashboard: PEO user detected, checking verification status");
          
          // Check PEO verification status
          const verificationStatus = await getPEOVerificationStatus(data.session.user.id);
          console.log("PEO verification status:", verificationStatus);
          
          if (verificationStatus === 'approved') {
            console.log("PEO user is approved, redirecting to PEO dashboard");
            navigate('/peo/dashboard');
          } else {
            console.log("PEO user is not approved, redirecting to restricted access page");
            navigate('/peo/restricted');
          }
          return;
        }
        
        console.log("Dashboard: Regular user detected, showing dashboard");
        setIsLoading(false);
      } catch (error) {
        console.error("Dashboard error:", error);
        setError(error instanceof Error ? error.message : "An error occurred while checking authentication");
        setIsLoading(false);
      }
    };
    
    // Only run the check if authentication is complete
    if (!authLoading && !isAdminLoading) {
      checkAuth();
    }
  }, [navigate, isAuthenticated, userRole, user, authLoading, isAdminLoading, isSuperAdmin, forceRefresh]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-white to-gray-50 p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        
        <div className="mt-8 bg-gray-50 p-4 rounded border max-w-md w-full">
          <h3 className="text-sm font-medium mb-2">Debug Information:</h3>
          <pre className="text-xs overflow-auto bg-gray-100 p-2 rounded">
            {JSON.stringify(debugInfo, null, 2)}
          </pre>
          <Button 
            onClick={() => navigate('/login')}
            className="mt-4 w-full bg-primary text-white px-4 py-2 rounded hover:bg-primary/90"
          >
            Return to Login
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading || authLoading || isAdminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-gray-50">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
          <span className="mt-4 block text-xl font-medium">Loading your dashboard...</span>
          <p className="text-sm text-muted-foreground mt-2">Please wait while we prepare your content</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <MainNavigation />
      <div className="flex-grow pt-16 bg-gradient-to-b from-white to-gray-50">
        <DashboardContent key={`dashboard-content-${forceRefresh}`} />
      </div>
      <Footer />
    </div>
  );
};

export default Dashboard;
